import React, { useState, useRef } from 'react';
import { X, CheckCircle, Upload, FileText, InfoIcon } from 'lucide-react';
import { Button } from './Button';
import { useLanguage } from '../../context/LanguageContext';
interface SpecialistCoordinationModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function SpecialistCoordinationModal({
  isOpen,
  onClose
}: SpecialistCoordinationModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    t
  } = useLanguage();
  if (!isOpen) return null;
  const sf = t.specialistForm;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isConfirmed) return;
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
    }, 1500);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileName(file ? file.name : '');
  };
  const handleClose = () => {
    setIsSuccess(false);
    setIsConfirmed(false);
    setFileName('');
    onClose();
  };
  const inputClass = 'w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-caces-accent focus:border-transparent outline-none text-sm transition-shadow';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const optionalClass = 'text-gray-400 font-normal';
  const sectionTitleClass = 'text-base font-bold text-caces-navy border-b border-gray-200 pb-2 mb-4 tracking-wide';
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-223">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" role="dialog" aria-modal="true" aria-label={sf.title} data-id="element-224">
        {/* Header */}
        <div className="bg-caces-navy p-6 flex justify-between items-start text-white shrink-0" data-id="element-225">
          <div className="pr-8" data-id="element-226">
            <h3 className="text-lg font-serif font-semibold leading-tight mb-2" data-id="element-227">
              {sf.title}
            </h3>
            <p className="text-sm text-white/70 leading-relaxed" data-id="element-228">
              {sf.subtitle}
            </p>
          </div>
          <button onClick={handleClose} className="text-white/80 hover:text-white transition-colors ml-4 mt-0.5 flex-shrink-0" aria-label="Close modal" data-id="element-229">
            <X size={22} data-id="element-230" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="overflow-y-auto flex-1 p-6 md:p-8" data-id="element-231">
          {isSuccess ? <div className="text-center py-10" data-id="element-232">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-233">
                <CheckCircle className="w-8 h-8 text-green-600" data-id="element-234" />
              </div>
              <h4 className="text-2xl font-serif font-bold text-caces-navy mb-2" data-id="element-235">
                {sf.success.title}
              </h4>
              <p className="text-gray-600 mb-8 max-w-md mx-auto" data-id="element-236">
                {sf.success.desc}
              </p>
              <Button onClick={handleClose} fullWidth data-id="element-237">
                {sf.success.close}
              </Button>
            </div> : <form onSubmit={handleSubmit} className="space-y-8" data-id="element-238">
              {/* ── Section I: Professional Credentials ── */}
              <div data-id="element-239">
                <h4 className={sectionTitleClass} data-id="element-240">{sf.section1.title}</h4>
                <div className="space-y-4" data-id="element-241">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-242">
                    <div data-id="element-243">
                      <label htmlFor="sf-name" className={labelClass} data-id="element-244">
                        {sf.section1.name}{' '}
                        <span className="text-red-400" data-id="element-245">*</span>
                      </label>
                      <input type="text" id="sf-name" required className={inputClass} placeholder={sf.section1.namePh} data-id="element-246" />
                    </div>
                    <div data-id="element-247">
                      <label htmlFor="sf-license" className={labelClass} data-id="element-248">
                        {sf.section1.license}{' '}
                        <span className="text-red-400" data-id="element-249">*</span>
                      </label>
                      <input type="text" id="sf-license" required className={inputClass} placeholder={sf.section1.licensePh} data-id="element-250" />
                    </div>
                  </div>

                  {/* Type of Request — full width */}
                  <div data-id="element-251">
                    <label htmlFor="sf-request-type" className={labelClass} data-id="element-252">
                      {sf.section1.requestType}{' '}
                      <span className="text-red-400" data-id="element-253">*</span>
                    </label>
                    <select id="sf-request-type" required className={inputClass} data-id="element-254">
                      <option value="" data-id="element-255">
                        {sf.section1.requestTypeOptions.placeholder}
                      </option>
                      <option value="first-time" data-id="element-256">
                        {sf.section1.requestTypeOptions.firstTime}
                      </option>
                      <option value="returning" data-id="element-257">
                        {sf.section1.requestTypeOptions.returning}
                      </option>
                      <option value="regular" data-id="element-258">
                        {sf.section1.requestTypeOptions.regular}
                      </option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-259">
                    <div data-id="element-260">
                      <label htmlFor="sf-specialty" className={labelClass} data-id="element-261">
                        {sf.section1.specialty}{' '}
                        <span className="text-red-400" data-id="element-262">*</span>
                      </label>
                      <select id="sf-specialty" required className={inputClass} data-id="element-263">
                        <option value="" data-id="element-264">{t.modal.form.options.general}</option>
                        <option data-id="element-265">{t.modal.form.options.laparoscopic}</option>
                        <option data-id="element-266">{t.modal.form.options.urological}</option>
                        <option data-id="element-267">{t.modal.form.options.colorectal}</option>
                        <option data-id="element-268">{t.modal.form.options.oncological}</option>
                        <option data-id="element-269">{t.modal.form.options.orthopedic}</option>
                        <option data-id="element-270">{t.modal.form.options.ent}</option>
                      </select>
                    </div>
                    <div data-id="element-271">
                      <label htmlFor="sf-institution" className={labelClass} data-id="element-272">
                        {sf.section1.institution}{' '}
                        <span className={optionalClass} data-id="element-273">(optional)</span>
                      </label>
                      <input type="text" id="sf-institution" className={inputClass} placeholder={sf.section1.institutionPh} data-id="element-274" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-275">
                    <div data-id="element-276">
                      <label htmlFor="sf-email" className={labelClass} data-id="element-277">
                        {sf.section1.email}{' '}
                        <span className="text-red-400" data-id="element-278">*</span>
                      </label>
                      <input type="email" id="sf-email" required className={inputClass} placeholder={sf.section1.emailPh} data-id="element-279" />
                    </div>
                    <div data-id="element-280">
                      <label htmlFor="sf-phone" className={labelClass} data-id="element-281">
                        {sf.section1.phone}{' '}
                        <span className="text-red-400" data-id="element-282">*</span>
                      </label>
                      <input type="tel" id="sf-phone" required className={inputClass} placeholder={sf.section1.phonePh} data-id="element-283" />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Section II: Surgical Planning ── */}
              <div data-id="element-284">
                <h4 className={sectionTitleClass} data-id="element-285">{sf.section2.title}</h4>
                <div className="space-y-4" data-id="element-286">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-287">
                    <div data-id="element-288">
                      <label htmlFor="sf-surgical-specialty" className={labelClass} data-id="element-289">
                        {sf.section2.surgicalSpecialty}{' '}
                        <span className="text-red-400" data-id="element-290">*</span>
                      </label>
                      <select id="sf-surgical-specialty" required className={inputClass} data-id="element-291">
                        <option value="" data-id="element-292">{t.modal.form.options.general}</option>
                        <option data-id="element-293">{t.modal.form.options.laparoscopic}</option>
                        <option data-id="element-294">{t.modal.form.options.urological}</option>
                        <option data-id="element-295">{t.modal.form.options.colorectal}</option>
                        <option data-id="element-296">{t.modal.form.options.oncological}</option>
                        <option data-id="element-297">{t.modal.form.options.orthopedic}</option>
                        <option data-id="element-298">{t.modal.form.options.ent}</option>
                      </select>
                    </div>
                    <div data-id="element-299">
                      <label htmlFor="sf-procedure" className={labelClass} data-id="element-300">
                        {sf.section2.procedure}{' '}
                        <span className="text-red-400" data-id="element-301">*</span>
                      </label>
                      <input type="text" id="sf-procedure" required className={inputClass} placeholder={sf.section2.procedurePh} data-id="element-302" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-303">
                    <div data-id="element-304">
                      <label htmlFor="sf-duration" className={labelClass} data-id="element-305">
                        {sf.section2.duration}{' '}
                        <span className={optionalClass} data-id="element-306">(optional)</span>
                      </label>
                      <select id="sf-duration" className={inputClass} data-id="element-307">
                        <option value="" data-id="element-308">
                          {sf.section2.durationOptions.placeholder}
                        </option>
                        <option data-id="element-309">{sf.section2.durationOptions.under1}</option>
                        <option data-id="element-310">{sf.section2.durationOptions.one2}</option>
                        <option data-id="element-311">{sf.section2.durationOptions.two3}</option>
                        <option data-id="element-312">{sf.section2.durationOptions.three4}</option>
                        <option data-id="element-313">{sf.section2.durationOptions.over4}</option>
                      </select>
                    </div>
                    <div data-id="element-314">
                      <label htmlFor="sf-date" className={labelClass} data-id="element-315">
                        {sf.section2.date}{' '}
                        <span className={optionalClass} data-id="element-316">(optional)</span>
                      </label>
                      <input type="date" id="sf-date" className={inputClass} data-id="element-317" />
                    </div>
                    <div data-id="element-318">
                      <label htmlFor="sf-hospitalization" className={labelClass} data-id="element-319">
                        {sf.section2.hospitalization}{' '}
                        <span className={optionalClass} data-id="element-320">(optional)</span>
                      </label>
                      <input type="number" id="sf-hospitalization" min="0" max="30" className={inputClass} placeholder={sf.section2.hospitalizationPh} data-id="element-321" />
                    </div>
                  </div>

                  {/* Ambulatory info note */}
                  <div className="flex items-start gap-2 mt-1" data-id="element-322">
                    <InfoIcon size={14} className="text-gray-400 mt-0.5 flex-shrink-0" data-id="element-323" />
                    <p className="text-xs text-gray-500 italic leading-relaxed" data-id="element-324">
                      {sf.section2.hospitalizationNote}
                    </p>
                  </div>
                </div>
              </div>

              {/* ── Section III: Technical & Institutional Requirements ── */}
              <div data-id="element-325">
                <h4 className={sectionTitleClass} data-id="element-326">{sf.section3.title}</h4>
                <div className="space-y-4" data-id="element-327">
                  <div data-id="element-328">
                    <label htmlFor="sf-requirements" className={labelClass} data-id="element-329">
                      {sf.section3.requirements}{' '}
                      <span className={optionalClass} data-id="element-330">(optional)</span>
                    </label>
                    <textarea id="sf-requirements" rows={3} className={inputClass} placeholder={sf.section3.requirementsPh} data-id="element-331"></textarea>
                  </div>

                  <div data-id="element-332">
                    <label className={labelClass} data-id="element-333">
                      {sf.section3.documents}{' '}
                      <span className={optionalClass} data-id="element-334">(optional)</span>
                    </label>
                    <div className="flex items-center gap-3" data-id="element-335">
                      <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-336">
                        <Upload size={16} data-id="element-337" />
                        {sf.section3.documentsBtn}
                      </button>
                      <span className="text-sm text-gray-500 truncate max-w-[200px]" data-id="element-338">
                        {fileName ? <span className="flex items-center gap-1" data-id="element-339">
                            <FileText size={14} className="text-caces-accent" data-id="element-340" />
                            {fileName}
                          </span> : sf.section3.noFile}
                      </span>
                      <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.png" onChange={handleFileChange} data-id="element-341" />
                    </div>
                    <p className="text-xs text-gray-400 mt-1" data-id="element-342">
                      {sf.section3.documentsNote}
                    </p>
                  </div>

                  {/* Review note */}
                  <div className="flex items-start gap-2" data-id="element-343">
                    <InfoIcon size={14} className="text-gray-400 mt-0.5 flex-shrink-0" data-id="element-344" />
                    <p className="text-xs text-gray-500 leading-relaxed" data-id="element-345">
                      {sf.section3.reviewNote}
                    </p>
                  </div>
                </div>
              </div>

              {/* ── Section IV: Institutional Confirmation ── */}
              <div data-id="element-346">
                <h4 className={sectionTitleClass} data-id="element-347">{sf.section4.title}</h4>
                <label className="flex items-start gap-3 cursor-pointer group" data-id="element-348">
                  <input type="checkbox" checked={isConfirmed} onChange={e => setIsConfirmed(e.target.checked)} className="mt-1 w-4 h-4 rounded border-gray-300 text-caces-accent focus:ring-caces-accent" required data-id="element-349" />
                  <span className="text-sm text-gray-700 leading-relaxed group-hover:text-gray-900" data-id="element-350">
                    {sf.section4.checkbox}
                  </span>
                </label>
              </div>

              {/* ── Why Operate at CACES ── */}
              <div className="bg-caces-sky/30 border border-caces-light/50 rounded-xl p-5" data-id="element-351">
                <h4 className="text-sm font-bold text-caces-navy mb-3" data-id="element-352">
                  {sf.whyOperate.title}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2.5" data-id="element-353">
                  {sf.whyOperate.items.map((item, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-354">
                      <CheckCircle size={16} className="text-caces-accent flex-shrink-0" data-id="element-355" />
                      <span className="text-sm text-gray-700" data-id="element-356">{item}</span>
                    </div>)}
                </div>
              </div>

              {/* Legal Disclaimer */}
              <div className="bg-gray-50 border border-gray-200 p-3 rounded-lg text-xs text-gray-500 leading-relaxed" data-id="element-357">
                <p data-id="element-358">{sf.note}</p>
              </div>

              <Button type="submit" fullWidth size="lg" isLoading={isSubmitting} disabled={!isConfirmed} data-id="element-359">
                {sf.submit}
              </Button>
            </form>}
        </div>
      </div>
    </div>;
}